import { FC, useContext, useEffect, useState } from 'react';

import { Button } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { FeaturesContext } from 'features';
import { LocalizationContext } from 'localization';
import { isSandbox } from 'sandbox';
import {
  createChatWidget,
  removeChatWidget,
  setUserData,
  subscribeToStateChange,
  toggleChatVisibility,
} from 'services/ChatWidget';
import { SettingsContext } from 'settings';

import s from './ChatWidget.module.scss';

export const ChatWidget: FC = () => {
  const { t } = useContext(LocalizationContext);

  const {
    data: { me, tokens },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { clubName },
    },
  } = useContext(SettingsContext);
  const { subscription } = useContext(FeaturesContext);

  const { first_name, last_name, phone } = me ?? {};
  const { access_token } = tokens ?? {};

  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isSandbox) {
      setIsReady(true);
      return;
    }

    createChatWidget().then(() => {
      setIsReady(true);

      subscribeToStateChange((state) => {
        if (state.toState === 'expanded') {
          setIsOpen(true);
        } else {
          setIsOpen(false);
        }
      });
    });

    return () => {
      removeChatWidget();
    };
  }, []);

  useEffect(() => {
    if (isSandbox) return;

    if (
      !first_name ||
      !last_name ||
      !phone ||
      !access_token ||
      !subscription ||
      !clubName
    ) {
      return;
    }

    const encodedToken = atob(access_token?.split('.')[1] ?? '');
    const clubId = encodedToken ? JSON.parse(encodedToken)?.company_id : null;

    setUserData({
      clubName,
      phone,
      clubId: clubId ?? 0,
      email: `${clubId ?? 0}-support@smartshell.gg`,
      firstName: first_name,
      lastName: last_name,
      subscriptionType: subscription,
    });
  }, [first_name, last_name, phone, access_token, subscription, clubName]);

  if (!isReady) return null;

  if (
    !first_name ||
    !last_name ||
    !phone ||
    !access_token ||
    !subscription ||
    !clubName
  ) {
    return null;
  }

  return (
    <Button
      onClick={() => {
        if (isSandbox) {
          setIsOpen((value) => !value);
          return;
        }
        toggleChatVisibility();
      }}
      className={s['blue-button']}
      size="s"
      variant="secondary"
      fullWidth
      iconName={isOpen ? 'CircleCloseIcon' : 'CommentIcon'}
      onlyIcon={false}
    >
      {t(isOpen ? 'support.close' : 'support.open')}
    </Button>
  );
};
